@forward '@raspberrypifoundation/design-system-core/scss/components/button';
@forward '@raspberrypifoundation/design-system-core/scss/properties/colors/text';
@forward '@raspberrypifoundation/design-system-core/scss/properties/colors/brand';
@forward '../../global/styles';

@use '@raspberrypifoundation/design-system-core/scss/mixins/typography' as typography;

:host {
  --arrow-icon-color: var(--rpf-text-primary);
  --button-hover-background-color: var(--rpf-grey-50);
  --button-active-background-color: var(--rpf-grey-100);
  --button-text-color: var(--rpf-text-primary);
  --button-border-color: var(--rpf-text-primary);
  --menu-item-text-color: var(--text-color);
  --button-selected-color: var(--rpf-grey-100);
  --menu-item-hover-background-color: var(--rpf-grey-50);
  --menu-item-active-background-color: var(--rpf-grey-100);
  --menu-focus-border-color: var(--focus-border-color);

  .rpf-button {
    --rpf-button-background-color: var(--text-color);
    --rpf-button-background-color-hover: var(--text-color);

    &:hover::before {
      background-color: var(--button-hover-background-color);
    }

    &:focus-visible::before {
      outline: var(--border-width-md) solid var(--focus-border-color);
    }

    &:active {
      color: var(--button-text-color);
    }

    &:active::before {
      background-color: var(--button-active-background-color);
    }
  }

  display: block;
}

:host([theme='dark']) {
  --arrow-icon-color: var(--rpf-white);
  --button-hover-background-color: var(--rpf-grey-700);
  --button-active-background-color: var(--rpf-grey-600);
  --button-text-color: var(--rpf-white);
  --button-border-color: var(--rpf-text-secondary);
  --menu-item-text-color: var(--text-color);
  --menu-item-hover-background-color: var(--rpf-grey-700);
  --menu-item-active-background-color: var(--rpf-grey-600);
  --menu-focus-border-color: var(--focus-border-color);

  .rpf-button {
    --rpf-button-text-color: var(--nav-background);
    --rpf-button-background-color-hover: var(--text-color);
  }
}

:host([theme='clear']) {
  --arrow-icon-color: var(--rpf-white);
  --button-text-color: var(--rpf-white);
  --button-hover-background-color: var(--transparent-black-100);
  --button-active-background-color: var(--transparent-black-200);
  --button-border-color: var(--rpf-white);
  --menu-item-text-color: var(--rpf-text-primary);
  --menu-item-hover-background-color: var(--rpf-grey-50);
  --menu-item-active-background-color: var(--rpf-grey-100);
  --menu-focus-border-color: var(--rpf-brand-raspberry);

  .rpf-button {
    --rpf-button-text-color: var(--nav-background);
    --rpf-button-background-color-hover: var(--rpf-white);
  }
}

.rpf-auth__buttons {
  align-items: center;
  block-size: 2rem;
  display: flex;
  flex-direction: row;
  gap: var(--space-2);
  justify-items: center;
  padding-inline: var(--space-1);

  a:hover {
    background-color: unset;
  }

  button {
    font-size: var(--button-font-size);
  }

  &--responsive-menu {
    block-size: auto;
    border-block-start: 1px solid var(--rpf-grey-150);
    flex-wrap: wrap;
    padding-block: var(--space-2);
    padding-inline: 0;

    > div {
      display: flex;
      flex: 1 1 auto;
      justify-content: center;
      min-inline-size: 197px;
    }

    form {
      inline-size: 100%;
    }

    button {
      @include typography.style-1-5(bold);

      inline-size: 100%;
      justify-content: center;
      min-block-size: var(--rpf-button-lg-height);

      &::before {
        inset: 0;
      }
    }
  }
}

.rpf-auth__sign-up-button {
  display: none;

  .rpf-button {
    &:active::before {
      border: none;
    }
  }

  .rpf-auth__buttons--responsive-menu & {
    display: flex;
  }

  @container (min-width: 35rem) {
    display: block;
  }
}

.rpf-auth__log-in-button {
  .rpf-button {
    &:active::before {
      border-color: var(--button-border-color);
    }
  }

  &--lg {
    display: none;

    .rpf-auth__buttons--responsive-menu & {
      display: flex;
    }
  }

  &--sm {
    align-items: center;
    background-color: var(--nav-background);
    block-size: 2rem;
    border: none;
    border-radius: var(--border-radius);
    border-width: var(--border-width-md);
    cursor: pointer;
    display: flex;
    padding-inline: 0.375rem;
    position: relative;

    &:hover {
      background-color: var(--button-hover-background-color);
    }

    &:active {
      background-color: var(--button-active-background-color);
    }

    &::after {
      // WCGAG guidance states that button targets should be a minimum of 44px x 44px;
      // This extends the hitbox vertically without affecting visual styles
      content: '';
      inset: -0.375rem;
      position: absolute;
    }

    &:focus-visible {
      outline: var(--border-width-md) solid;
      outline-color: var(--focus-border-color);
    }

    svg {
      path {
        fill: var(--text-color);
      }
    }

    .rpf-auth__buttons--responsive-menu & {
      display: none;
    }
  }

  .rpf-auth__buttons--responsive-menu & {
    form {
      inline-size: 100%;
    }
  }

  @container (min-width: 17.5rem) {
    &--lg {
      display: flex;
    }

    &--sm {
      display: none;
    }
  }
}

.rpf-auth__log-out-button {
  .rpf-button {
    &:active::before {
      border-color: var(--button-border-color);
    }
  }
}

.rpf-auth__your_account_dropdown {
  align-items: center;
  block-size: 1.25rem;
  display: flex;
  font-family: var(--font-family);
  gap: 0.5rem;

  svg {
    path {
      fill: var(--arrow-icon-color);
    }
  }

  &__account_icon {
    display: flex;

    @container (min-width: 22.5rem) {
      display: none;
    }
  }

  &__arrow_icon {
    display: none;

    @container (min-width: 22.5rem) {
      display: flex;
    }
  }

  &__text {
    display: none;
    font-size: var(--button-font-size);
    font-weight: var(--font-weight-bold);
    text-wrap: nowrap;

    @container (min-width: 22.5rem) {
      display: block;
    }
  }
}

.rpf-auth__menu {
  color: var(--rpf-text-secondary);
  display: flex;
  flex-direction: column;
  font-size: var(--font-size-1);
  font-weight: var(--font-weight-bold);
  inline-size: 100%;
  padding: var(--space-1-5);
  scrollbar-color: var(--rpf-grey-400) transparent;

  svg {
    path {
      fill: var(--menu-item-text-color);
    }
  }

  &--responsive-menu {
    border-block-start: 1px solid var(--rpf-grey-150);
    display: block;
    inline-size: auto;
    padding-block: var(--space-2);
    padding-inline: 0;

    ul {
      align-items: center;
      flex-direction: row;
      flex-wrap: wrap;
      gap: var(--space-2);
      inline-size: 100%;
    }

    li {
      flex: 1 1 auto;
      inline-size: auto;
      min-inline-size: 197px;

      &:last-of-type {
        a {
          border-color: var(--rpf-text-primary);
          border-radius: var(--border-radius);
          border-style: solid;
          border-width: var(--border-width-md);
        }
      }
    }

    a {
      @include typography.style-1-5(bold);

      color: var(--rpf-text-primary);
      justify-content: center;
      min-block-size: 48px;
      padding-block: 0;

      span {
        text-wrap: auto;
      }
    }
  }
}

ul {
  align-items: flex-start;
  box-sizing: border-box;
  color: var(--rpf-text-secondary);
  display: flex;
  flex-direction: column;
  gap: 0;
  list-style: none;
  margin: 0;
  overflow-y: auto;
  padding: 0;
  scrollbar-color: var(--rpf-grey-400) transparent;
}

li {
  display: flex;
  inline-size: 100%;
}

a {
  align-items: center;
  border-color: transparent;
  border-radius: var(--border-radius);
  border-style: solid;
  border-width: var(--border-width-md);
  box-sizing: border-box;
  color: var(--menu-item-text-color);
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  font-size: var(--font-size-1);
  font-size: var(--button-font-size);
  gap: var(--space-1);
  justify-content: center;
  justify-content: flex-start;
  padding-block: var(--space-1-5);
  padding-inline: var(--space-1-5);
  text-decoration: none;
  word-wrap: break-word;

  span {
    display: flex;
    text-wrap: nowrap;
  }

  &:hover {
    background-color: var(--menu-item-hover-background-color);
  }

  &:active {
    background-color: var(--menu-item-active-background-color);
  }

  &:focus-visible {
    background-color: transparent;
    border-color: var(--menu-focus-border-color);
    outline-style: none;
  }
}
